import React, { useState, useEffect, useContext } from "react";
import { useNavigate } from "react-router-dom";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faHashtag,
  faFire,
  faUsers,
  faSearch,
  faFilter,
} from "@fortawesome/free-solid-svg-icons";
import AutoScrollable from "../global/AutoScrollable";
import { LoginContext } from "./Login";
import { getCookie } from "../global/Global";
import Search from "./Search";
import useDidUpdateEffect from "../global/DidUpdateEffect";
import "./Tags.css";

const Tags = () => {
  const [tags, setTags] = useState([]);
  const [searchQuery, setSearchQuery] = useState("");
  const [minUseCount, setMinUseCount] = useState(1);
  const [loading, setLoading] = useState(false);
  const { user: myUser } = useContext(LoginContext);
  const navigate = useNavigate();

  useDidUpdateEffect(() => {
    logT("tags", "search query changed", searchQuery);
    fetchData(true);
  }, [searchQuery, minUseCount]);

  const fetchData = async (full = false) => {
    if (loading) return;
    setLoading(true);

    let index = (tags && tags.length) || 0;
    let limit = 50;
    if (full) {
      index = 0;
      limit = Math.max(50, tags.length);
    }

    const token = getCookie("token") || "";
    const userid = parseInt(getCookie("userid")) || 0;

    try {
      const data = await window.TALKVIOAPI("tags", {
        index,
        limit,
        token,
        userid,
        search: searchQuery?.length > 0 ? searchQuery : undefined,
        minUseCount: minUseCount > 1 ? minUseCount : undefined,
      });

      if (!full) {
        setTags((tags || []).concat(data.tags || []));
      } else {
        setTags(data.tags || []);
      }
    } catch (error) {
      logTE("tags", "failed to fetch tags", error);
    }

    setLoading(false);
    document.title = `${__("Popular Tags")} - Talkvio`;
  };

  const handleTagClick = (tagText) => {
    navigate(`/tag/${encodeURIComponent(tagText)}`);
  };

  const formatNumber = (num) => {
    if (num >= 1000000) {
      return (num / 1000000).toFixed(1) + "M";
    } else if (num >= 1000) {
      return (num / 1000).toFixed(1) + "K";
    }
    return num.toString();
  };

  const getPopularityLevel = (popularity) => {
    if (popularity >= 5) return "very-high";
    if (popularity >= 4) return "high";
    if (popularity >= 2) return "medium";
    if (popularity >= 0) return "low";
    return "very-low";
  };

  useEffect(() => {
    fetchData();
  }, []);

  return (
    <div className="tagsViewContainer">
      <div className="mainTitle">
        <div className="titleText">
          <FontAwesomeIcon icon={faHashtag} className="title-icon" />
          {__("Popular Tags")}
        </div>
      </div>

      <div className="tagsControls">
        <div className="searchBox">
          <Search
            onSeach={(text) => {
              setSearchQuery(text);
            }}
            noClearInput={true}
            autoSearch={true}
          />
        </div>

        <div className="filterBox">
          <div className="filterLabel">
            <FontAwesomeIcon icon={faFilter} />
            {__("Min Posts")}:
          </div>
          <select
            value={minUseCount}
            onChange={(event) => {
              const value = parseInt(event.target.value);
              setMinUseCount(value);
            }}
            className="filterSelect"
          >
            <option value="1">{__("Any")}</option>
            <option value="5">5+</option>
            <option value="10">10+</option>
            <option value="25">25+</option>
            <option value="50">50+</option>
            <option value="100">100+</option>
            <option value="500">500+</option>
          </select>
        </div>
      </div>

      <AutoScrollable onBottomScroll={() => fetchData()}>
        <div className="tagsGrid">
          {tags.map((tag) => (
            <div
              key={tag.tagid}
              className={`tagCard ${getPopularityLevel(tag.popularity)}`}
              onClick={() => handleTagClick(tag.tagtext)}
            >
              <div className="tagCard-header">
                <div className="tagCard-icon">
                  <FontAwesomeIcon icon={faHashtag} />
                </div>
                <div className="tagCard-name">{tag.tagtext}</div>
                <div
                  className={`tagCard-popularity ${getPopularityLevel(tag.popularity)}`}
                >
                  <FontAwesomeIcon icon={faFire} />
                </div>
              </div>

              <div className="tagCard-stats">
                <div className="tagCard-stat">
                  <div className="stat-value">{formatNumber(tag.usecount)}</div>
                  <div className="stat-label">
                    <FontAwesomeIcon icon={faUsers} className="stat-icon" />
                    {__("Posts")}
                  </div>
                </div>
                <div className="tagCard-stat">
                  <div className="stat-value">
                    {(tag.popularity * 5).toFixed(2)}
                  </div>
                  <div className="stat-label">
                    <FontAwesomeIcon icon={faFire} className="stat-icon" />
                    {__("Score")}
                  </div>
                </div>
              </div>

              <div className="tagCard-footer">
                <div className="tagCard-date">
                  {__("Created")}:{" "}
                  {new Date(tag.dateline * 1000).toLocaleDateString()}
                </div>
              </div>
            </div>
          ))}
        </div>

        {loading && (
          <div className="tagsLoading">
            <div className="loading-spinner"></div>
            <div>{__("Loading tags")}...</div>
          </div>
        )}

        {tags.length === 0 && !loading && (
          <div className="tagsEmpty">
            <FontAwesomeIcon icon={faHashtag} className="empty-icon" />
            <div className="empty-title">{__("No tags found")}</div>
            <div className="empty-message">
              {searchQuery
                ? __("Try adjusting your search or filters")
                : __("No tags available at the moment")}
            </div>
          </div>
        )}
      </AutoScrollable>
    </div>
  );
};

export default Tags;
